import { gql } from 'graphql-tag';
import React from 'react';
import get from 'lodash.get';
import { useRouteMatch } from 'react-router-dom';
import { useQuery } from '@apollo/client';

import { RedirectToUnhandledError } from '~/modules/common/unhandled-error';
import { projectDate2Fragment } from '~/modules/resourcing/common/fragments/specificProjectFragment';
import { RESOURCE_ALLOCATION_PERMISSION } from '~/modules/common/enums';
import { useMeContext } from '~/modules/me/useMeContext';

export const projectQuery = gql`
  query getProjectBySlug(
    $projectSlug: String!
    $includeResourceAllocationActions: Boolean = false
    $isPsaRmpEnhancedProjectManagerPersonaEnabled: Boolean = false
    $scope: ScopeObjectType
  ) {
    project(projectSlug: $projectSlug) {
      ...SpecificProjectDate2Fragment
      resourceAllocationActions
        @include(if: $includeResourceAllocationActions) {
        canViewResourceAllocation
        canEditResourceAllocation
      }
      defaultGroupRestrictions(scope: $scope)
        @include(if: $isPsaRmpEnhancedProjectManagerPersonaEnabled) {
        location {
          id
          displayText
        }
        division {
          id
          displayText
        }
        costCenter {
          id
          displayText
        }
        serviceCenter {
          id
          displayText
        }
        department {
          id
          displayText
        }
        employeeType {
          id
          displayText
        }
      }
      copyProjectJobId
      budgetedCost {
        currency {
          id
          displayText
        }
      }
    }
  }
  ${projectDate2Fragment}
`;

export const useProjectInfo = ({ scope } = {}) => {
  const {
    params: { slug }
  } = useRouteMatch();

  const {
    permissionsMap,
    featureFlags: { isPsaRmpEnhancedProjectManagerPersonaEnabled }
  } = useMeContext();

  const canViewResourceAllocation = Boolean(
    permissionsMap[RESOURCE_ALLOCATION_PERMISSION.VIEW_RESOURCE_ALLOCATION]
  );

  const { loading, data, error } = useQuery(projectQuery, {
    variables: {
      projectSlug: slug,
      includeResourceAllocationActions:
        isPsaRmpEnhancedProjectManagerPersonaEnabled &&
        canViewResourceAllocation,
      isPsaRmpEnhancedProjectManagerPersonaEnabled,
      scope
    }
  });

  if (error) return <RedirectToUnhandledError />;

  const project = get(data, 'project', {});

  return { project, isLoading: loading, error };
};
